import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { listReleases } from "../../../api/knowledgeData/releasesApi";
import { formatTimeAgo } from "../../../shared/formatting/relativeTimes";
import { useLoad } from "../../../shared/loading/useLoad";
import { appPaths } from "../../../shared/routing/appPaths";
import AppButton from "../../../ui/buttons/AppButton";
import DropdownChevron from "../../../ui/overlays/DropdownChevron";
import MenuButton, { type MenuItem } from "../../../ui/overlays/MenuButton";
import { onReleasesChanged } from "./releaseChanges";

/** The project's releases, opened from the Data model page's header (K-12, F77, design pass
 * section 9): their count on the button, newest first in the list, each opening its release
 * page. New ones are made with "Release…" beside it, by those who may (F17). */
export default function ReleasesMenu({ projectId }: { projectId: string }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const load = useCallback((signal: AbortSignal) => listReleases(projectId, signal), [projectId]);
  const releases = useLoad(load);
  const { refresh } = releases;
  useEffect(() => onReleasesChanged(refresh), [refresh]);
  if (releases.state.status !== "loaded") return null;
  const listed = releases.state.value;
  // A menu opens on an item it can choose, so with none there is no menu: the header says so.
  if (listed.length === 0) return <span className="app-text-muted text-sm">{t("data.release.none")}</span>;
  const items: MenuItem[] = listed.map((release) => ({
    id: `${release.datasetId}/${String(release.version.number)}`,
    label: `${t("data.release.numberWithTitle", { number: release.version.number, title: release.title })} · ${formatTimeAgo(release.version.createdAt, i18n.resolvedLanguage)}`,
    action: { kind: "select", onSelect: () => { void navigate(appPaths.release(release.datasetId, release.version.number)); } },
  }));
  return (
    <MenuButton
      align="right"
      width="lg"
      sections={[{ id: "releases", items }]}
      renderTrigger={(trigger, open) => (
        <AppButton {...trigger} size="sm">
          {t("ontologyViewer.releases.count", { count: listed.length })}
          <DropdownChevron open={open} />
        </AppButton>
      )}
    />
  );
}
